import type { MtmWidgetProfile } from './types';
import {
  assertMtmWidgetProfileComplete,
} from './mtmWidgetProfileValidation';
import { buildMtmWidgetSessionState } from './buildMtmWidgetSessionState';
import { syncMtmWidgetSessionMetadata } from './syncMtmWidgetSessionMetadata';

const SAIA_API_BASE = 'https://saia.3dlook.me/api/v2';
const SESSION_CACHE_TTL_MS = 10 * 60 * 1000;

export type MtmWidgetSession = {
  uuid: string;
  flowId: number;
  mtmClientId: number;
  widgetHost: string;
  accountId: string;
  personId: string;
  email: string;
  returnUrl: string;
};

type CachedMtmWidgetSession = {
  cacheKey: string;
  session: MtmWidgetSession;
  expiresAt: number;
};

let cachedMtmWidgetSession: CachedMtmWidgetSession | null = null;
let inFlightSessionPromise: Promise<MtmWidgetSession> | null = null;
let inFlightSessionKey: string | null = null;

function buildSessionCacheKey(publicKey: string, profile: MtmWidgetProfile, returnUrl: string) {
  const { defaultValues, customSettings, userContext } = profile;

  return [
    publicKey,
    defaultValues.email,
    defaultValues.heightCm,
    defaultValues.weight,
    customSettings?.gender,
    userContext?.accountId ?? 'guest',
    userContext?.personId ?? 'no-person',
    profile.firstName ?? 'no-name',
    returnUrl,
  ].join(':');
}

function readCachedSession(cacheKey: string): MtmWidgetSession | null {
  if (!cachedMtmWidgetSession) {
    return null;
  }

  if (cachedMtmWidgetSession.cacheKey !== cacheKey || cachedMtmWidgetSession.expiresAt <= Date.now()) {
    cachedMtmWidgetSession = null;
    return null;
  }

  return cachedMtmWidgetSession.session;
}

function writeCachedSession(cacheKey: string, session: MtmWidgetSession) {
  cachedMtmWidgetSession = {
    cacheKey,
    session,
    expiresAt: Date.now() + SESSION_CACHE_TTL_MS,
  };
}

export function clearMtmWidgetSessionCache() {
  cachedMtmWidgetSession = null;
  inFlightSessionPromise = null;
  inFlightSessionKey = null;
}

function readApiErrorMessage(payload: unknown, fallback: string) {
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const detail = record.detail ?? record.message ?? record.error;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
  }

  return fallback;
}

async function parseApiResponse<T>(response: Response, fallbackMessage: string): Promise<T> {
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(readApiErrorMessage(payload, fallbackMessage));
  }

  return payload as T;
}

async function fetchWithRetry(url: string, init: RequestInit, fallbackMessage: string): Promise<Response> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const response = await fetch(url, init);
      return response;
    } catch (error: unknown) {
      lastError = error instanceof Error ? error : new Error(fallbackMessage);
      if (attempt === 0) {
        await new Promise((resolve) => window.setTimeout(resolve, 400));
      }
    }
  }

  throw lastError ?? new Error(fallbackMessage);
}

export {
  assertMtmWidgetProfileComplete,
  isMtmWidgetProfileComplete,
} from './mtmWidgetProfileValidation';

export async function createMtmWidgetSession(
  publicKey: string,
  profile: MtmWidgetProfile,
  options: { returnUrl?: string } = {},
): Promise<MtmWidgetSession> {
  assertMtmWidgetProfileComplete(profile);

  const email = profile.defaultValues.email ?? profile.userContext?.email ?? '';
  const firstName = profile.firstName?.trim() || null;
  const returnUrl = options.returnUrl ?? (typeof window !== 'undefined' ? window.location.href : '');

  const createPayload = await parseApiResponse<{
    uuid: string;
    id: number;
    absolute_url?: string;
  }>(
    await fetchWithRetry(
      `${SAIA_API_BASE}/persons/widget/`,
      {
        method: 'POST',
        headers: {
          Authorization: `PUBLIC ${publicKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          ...(firstName ? { firstName } : {}),
          state: buildMtmWidgetSessionState(profile, {
            status: 'set metadata',
            processStatus: '',
          }),
        }),
      },
      'Failed to create 3DLOOK widget session',
    ),
    'Failed to create 3DLOOK widget session',
  );

  if (!createPayload.uuid || !createPayload.id) {
    throw new Error('3DLOOK did not return a valid widget session');
  }

  const accountId = profile.userContext?.accountId ?? '';
  const personId = profile.userContext?.personId ?? '';

  const mtmClient = await parseApiResponse<{ id: number }>(
    await fetchWithRetry(
      `${SAIA_API_BASE}/measurements/mtm-clients/`,
      {
        method: 'POST',
        headers: {
          Authorization: `UUID ${createPayload.uuid}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ source: 'widget' }),
      },
      'Failed to create 3DLOOK MTM client',
    ),
    'Failed to create 3DLOOK MTM client',
  );

  if (!mtmClient.id) {
    throw new Error('3DLOOK did not return a valid MTM client');
  }

  const widgetHost = createPayload.absolute_url
    ? `${new URL(createPayload.absolute_url).protocol}//${new URL(createPayload.absolute_url).hostname}`
    : 'https://mtm-widget.3dlook.me';

  const session: MtmWidgetSession = {
    uuid: createPayload.uuid,
    flowId: createPayload.id,
    mtmClientId: mtmClient.id,
    widgetHost,
    accountId,
    personId,
    email,
    returnUrl,
  };

  await syncMtmWidgetSessionMetadata(session, profile);

  return session;
}

export async function getOrCreateMtmWidgetSession(
  publicKey: string,
  profile: MtmWidgetProfile,
  options: { returnUrl?: string; forceRefresh?: boolean } = {},
): Promise<MtmWidgetSession> {
  assertMtmWidgetProfileComplete(profile);

  const returnUrl = options.returnUrl ?? (typeof window !== 'undefined' ? window.location.href : '');
  const cacheKey = buildSessionCacheKey(publicKey, profile, returnUrl);

  if (options.forceRefresh) {
    if (cachedMtmWidgetSession?.cacheKey === cacheKey) {
      cachedMtmWidgetSession = null;
    }
    inFlightSessionPromise = null;
    inFlightSessionKey = null;
  } else {
    const cached = readCachedSession(cacheKey);
    if (cached) {
      return cached;
    }

    if (inFlightSessionPromise && inFlightSessionKey === cacheKey) {
      return inFlightSessionPromise;
    }
  }

  const sessionPromise = createMtmWidgetSession(publicKey, profile, { returnUrl })
    .then((session) => {
      writeCachedSession(cacheKey, session);
      return session;
    })
    .finally(() => {
      if (inFlightSessionKey === cacheKey) {
        inFlightSessionPromise = null;
        inFlightSessionKey = null;
      }
    });

  inFlightSessionPromise = sessionPromise;
  inFlightSessionKey = cacheKey;

  return sessionPromise;
}

export function buildMtmMobileScanUrl(
  session: MtmWidgetSession,
  options: { returnUrl?: string; origin?: string; skipCheckIn?: boolean } = {},
): string {
  const origin = options.origin ?? (typeof window !== 'undefined' ? window.location.origin : '');
  const returnUrl = options.returnUrl ?? session.returnUrl ?? (typeof window !== 'undefined' ? window.location.href : '');
  const params = [
    `origin=${origin}`,
    `returnUrl=${encodeURIComponent(returnUrl)}`,
    `mtmClientId=${session.mtmClientId}`,
  ];
  // 3DLOOK's /mobile/:id route uses the widget session UUID (redux flowId), not the numeric widget id.
  const hashRoute = options.skipCheckIn
    ? `#/mobile/${encodeURIComponent(session.uuid)}?`
    : '#/?';

  return `${session.widgetHost}/?key=${encodeURIComponent(session.uuid)}${hashRoute}${params.join('&')}`;
}

export function buildMtmWidgetLaunchUrl(options: {
  session: MtmWidgetSession;
  returnUrl: string;
  origin: string;
  isMobile: boolean;
  returnUrlDesktop?: boolean | string;
  photosFromGallery?: boolean;
}) {
  const { session, returnUrl, origin, returnUrlDesktop, photosFromGallery } = options;
  const params = [
    `origin=${origin}`,
    `returnUrl=${encodeURIComponent(returnUrl)}`,
    `mtmClientId=${session.mtmClientId}`,
  ];

  if (returnUrlDesktop) {
    params.push(`returnUrlDesktop=${String(returnUrlDesktop)}`);
  }

  if (photosFromGallery) {
    params.push('photosFromGallery=true');
  }

  return `${session.widgetHost}/?key=${encodeURIComponent(session.uuid)}#/?${params.join('&')}`;
}
